import { shareApi } from '@/lib/api';
import type { BudgetShare, CreateShareRequest, ShareRole } from '@/types';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const NAME_PATTERN = /^[a-zA-Z0-9_]+$/;

/**
 * Share service for managing budget sharing.
 * Requires online connectivity.
 */
export const ShareService = {
  async getInvitations() {
    if (!navigator.onLine) {
      throw new Error('Share management requires an internet connection');
    }
    return shareApi.getInvitations();
  },

  async getSharesForBudget(budgetId: string): Promise<BudgetShare[]> {
    if (!navigator.onLine) {
      throw new Error('Share management requires an internet connection');
    }
    return shareApi.getByBudget(budgetId);
  },

  async invite(request: CreateShareRequest): Promise<BudgetShare> {
    if (!navigator.onLine) {
      throw new Error('Sending invitations requires an internet connection');
    }

    const errors = this.validateCreateRequest(request);
    if (errors.length > 0) {
      throw new Error(errors.join(', '));
    }

    return shareApi.create(request);
  },

  async accept(shareId: string): Promise<BudgetShare> {
    if (!navigator.onLine) {
      throw new Error('Accepting invitations requires an internet connection');
    }
    return shareApi.accept(shareId);
  },

  async reject(shareId: string): Promise<void> {
    if (!navigator.onLine) {
      throw new Error('Rejecting invitations requires an internet connection');
    }
    await shareApi.delete(shareId);
  },

  async revoke(shareId: string): Promise<void> {
    if (!navigator.onLine) {
      throw new Error('Revoking access requires an internet connection');
    }
    await shareApi.delete(shareId);
  },

  async updateRole(shareId: string, role: ShareRole): Promise<BudgetShare> {
    if (!navigator.onLine) {
      throw new Error('Updating roles requires an internet connection');
    }
    return shareApi.updateRole(shareId, role);
  },

  validateCreateRequest(request: CreateShareRequest): string[] {
    const errors: string[] = [];

    if (!request.budgetId) {
      errors.push('Budget is required');
    }

    const invitee = request.invitee?.trim();
    if (!invitee) {
      errors.push('Email or name is required');
    } else if (invitee.includes('@')) {
      if (!this.isValidEmail(invitee)) {
        errors.push('Invalid email format');
      }
    } else if (!NAME_PATTERN.test(invitee)) {
      errors.push('Name can only contain letters, numbers, and underscores');
    }

    if (request.role !== 'EDITOR') {
      errors.push('Only editor role is supported');
    }

    return errors;
  },

  isValidEmail(email: string): boolean {
    return EMAIL_PATTERN.test(email);
  },

  getRoleName(role: ShareRole): string {
    const names: Record<ShareRole, string> = {
      OWNER: 'Owner',
      EDITOR: 'Editor',
    };
    return names[role] ?? role;
  },

  canEdit(role: ShareRole): boolean {
    return role === 'OWNER' || role === 'EDITOR';
  },

  canManageShares(role: ShareRole): boolean {
    return role === 'OWNER';
  },

  canDelete(role: ShareRole): boolean {
    return role === 'OWNER';
  },
};
